/**
 * cypAdhdMedicalScreeningConfig.js
 *
 * WLC's treatment contraindication screening for the CYP ADHD Clinical
 * Interview (cypAdhdFlow.medicalScreening === 'contraindicationsV1').
 *
 * What it is: the NICE NG87 cardiac and medical checks a prescriber needs
 * before ADHD medication can be considered. The clinician records a Yes/No
 * for each item during the Clinical Interview, with free-text detail where
 * Yes. The answers are read back on the MDT Review (so the diagnostic
 * decision meeting sees them) and on the PAD Health Checks page (so the
 * prescriber sees them next to the readings they are taking that day).
 *
 * What it is NOT (regulatory boundary): this module never computes a
 * clinical conclusion. It does not say "safe to prescribe" or flag an item
 * as a contraindication; it records what the clinician decided and shows
 * it back. Nothing here is fed to the AI drafting step, and nothing is
 * written into a report.
 *
 * Where the items live on the page: they are split across two existing CI
 * steps rather than given a step of their own, because that is where the
 * clinician is already talking to the parent about that subject. The nine
 * Medical History items render as a panel at the foot of the Medical History
 * section; the single Family History item renders at the foot of the Family
 * History section. `groupForSection` is the lookup the CI page uses.
 *
 * Content is from the WLC specification (Oct 2026). Change the wording only
 * against an updated specification. If the item list itself changes, that
 * is a NEW workflow state (contraindicationsV2), not an edit to this list:
 * patients already stamped V1 must keep seeing the list their answers were
 * recorded against.
 *
 * Stored shape (assessmentStages.clinicalInterview.medicalScreening):
 *   {
 *     items: {
 *       <itemId>: { present: true | false | null, detail: '' },
 *       ...
 *     }
 *   }
 * Only rows the clinician has touched exist in `items`. `present: null` (or
 * a missing item) means "not yet recorded"; the summary counts those apart
 * from No so an unfinished screen is visible rather than read as clear.
 */

const item = (id, label, detailHint) => Object.freeze({ id, label, detailHint });

const group = (id, sectionVarName, title, items) =>
  Object.freeze({ id, sectionVarName, title, items: Object.freeze(items) });

/**
 * Shown under the panel title on the Medical History group so the clinician
 * knows why these items sit inside an ADHD interview. Plain language: a
 * parent may be reading over the clinician's shoulder.
 */
export const CYP_MEDICAL_SCREENING_INTRO =
  'Record whether any of the following apply. These are the checks a '
  + 'prescriber needs before ADHD medication can be considered. Where Yes, '
  + 'add the detail a prescriber would need.';

/**
 * Footer under the read-back tables (MDT Review, PAD Health Checks). Says
 * where the answers came from and that they are not edited here, so a
 * reader looking for the edit control knows to go to the Clinical
 * Interview.
 */
export const CYP_MEDICAL_SCREENING_SOURCE_NOTE =
  'Recorded by the clinician during the Clinical Interview (Medical History '
  + 'and Family History). To change an answer, edit it there.';

/** Default hint shown in the detail box when an item has no specific one. */
const DEFAULT_DETAIL_HINT = 'Add detail if Yes';

/**
 * The ordered groups. The order inside each group is the order on screen
 * and in the read-back tables, so it is clinical content too.
 *
 * Group ids double as stable keys for tests and read-back headings;
 * sectionVarName is the CI data-model section each group belongs under
 * (python-functions/constants/cyp_adhd/ci_data_model.py CI_SECTION_LAYOUT).
 */
export const CYP_MEDICAL_SCREENING_GROUPS = Object.freeze([
  group('medicalHistory', 'medicalHistory', 'Cardiac and medical screening', [
    item('epilepsy', 'Epilepsy', DEFAULT_DETAIL_HINT),
    item(
      'congenitalHeart',
      'History of congenital heart disease or previous cardiac surgery',
      DEFAULT_DETAIL_HINT,
    ),
    item(
      'shortnessOfBreath',
      'Shortness of breath on exertion compared with peers',
      DEFAULT_DETAIL_HINT,
    ),
    item(
      'faintingOnExertion',
      'Fainting on exertion or in response to fright or noise',
      DEFAULT_DETAIL_HINT,
    ),
    item(
      'palpitations',
      'Palpitations that are rapid, regular, and start and stop suddenly',
      DEFAULT_DETAIL_HINT,
    ),
    item('chestPain', 'Chest pain suggesting cardiac origin', DEFAULT_DETAIL_HINT),
    item('thyroidDisease', 'Thyroid disease', DEFAULT_DETAIL_HINT),
    item('glaucoma', 'Glaucoma', DEFAULT_DETAIL_HINT),
    item(
      'allergies',
      'Allergies (environmental, medication or food)',
      'Nature and severity of past reactions, and whether an emergency plan is needed',
    ),
  ]),
  group('familyHistory', 'familyHistory', 'Cardiac family history', [
    item(
      'familyCardiacDeath',
      'History of cardiac death in family members at a young age',
      DEFAULT_DETAIL_HINT,
    ),
  ]),
]);

/** Every item across every group, in on-screen order. */
export const CYP_MEDICAL_SCREENING_ITEMS = Object.freeze(
  CYP_MEDICAL_SCREENING_GROUPS.flatMap((g) => g.items),
);

/** The stored shape before the clinician has answered anything. */
export const EMPTY_MEDICAL_SCREENING = Object.freeze({ items: Object.freeze({}) });

/**
 * The group to render at the foot of a CI data section, or null when that
 * section carries no screening items. The CI page calls this with
 * activeSection.sectionVarName on every render.
 */
export function groupForSection(sectionVarName) {
  if (!sectionVarName) return null;
  return CYP_MEDICAL_SCREENING_GROUPS.find((g) => g.sectionVarName === sectionVarName) || null;
}

/**
 * Coerce whatever is on the stage document into the shape the panel and
 * summary expect. Absent, null, a non-object, or a document missing `items`
 * all become `{ items: {} }`, so the components never branch on "is this
 * the old shape". Known-good data passes through with the same `items`
 * reference (no copy), so React memoisation on it still holds.
 */
export function normalizeMedicalScreeningData(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { items: {} };
  const items = raw.items;
  if (!items || typeof items !== 'object' || Array.isArray(items)) return { items: {} };
  return { items };
}

/**
 * Labels of every item currently answered Yes, in on-screen order across
 * all groups. Used by the read-outs and tests; a Yes is `present === true`
 * exactly (a truthy string stored by mistake does not count).
 */
export function listYesScreeningItems(items = {}) {
  return CYP_MEDICAL_SCREENING_ITEMS
    .filter((it) => items?.[it.id]?.present === true)
    .map((it) => it.label);
}

/**
 * Per-group answer counts. `yes` and `no` are explicit clinician answers;
 * `unrecorded` is anything else (never answered, or a stored value that is
 * neither true nor false). The three always sum to `total`, the number of
 * items in the group.
 */
export function countAnswered(group, items = {}) {
  let yes = 0;
  let no = 0;
  let unrecorded = 0;
  (group?.items || []).forEach((it) => {
    const present = items?.[it.id]?.present;
    if (present === true) yes += 1;
    else if (present === false) no += 1;
    else unrecorded += 1;
  });
  return { yes, no, unrecorded, total: yes + no + unrecorded };
}

/**
 * The one-line count shown under the read-back title, across all the groups
 * passed in. Three cases rather than one template, so a screen with nothing
 * recorded reads as unfinished instead of as "0 of 10" (which would look
 * like a clean bill):
 *   - no Yes:            "No items recorded as Yes"
 *   - some or all Yes:   "2 of 10 items recorded as Yes"
 *   - any unrecorded:    "; 3 items not yet recorded" is appended.
 */
export function medicalScreeningCountLine(groups, items = {}) {
  const totals = (groups || []).reduce(
    (acc, g) => {
      const c = countAnswered(g, items);
      return {
        yes: acc.yes + c.yes,
        unrecorded: acc.unrecorded + c.unrecorded,
        total: acc.total + c.total,
      };
    },
    { yes: 0, unrecorded: 0, total: 0 },
  );
  const head = totals.yes === 0
    ? 'No items recorded as Yes'
    : `${totals.yes} of ${totals.total} items recorded as Yes`;
  if (totals.unrecorded === 0) return head;
  const noun = totals.unrecorded === 1 ? 'item' : 'items';
  return `${head}; ${totals.unrecorded} ${noun} not yet recorded`;
}
